import { ENVIRONMENT, WEB2_BASE_URL } from "@helper/getEnvVariables";

export const isClientSide: boolean = typeof window !== "undefined";

export const getCookieByName = (name: string): string | undefined => {
  if (typeof window === "undefined") {
    return;
  }
  const match = document.cookie.match(new RegExp(name + "=([^;]+)"));
  return match ? match[1] : undefined;
};

export const setCookieValue = (
  cname: string,
  cvalue: string,
  expdays?: number,
): void => {
  if (typeof window === "undefined") {
    return;
  }
  let expires = "";
  if (expdays) {
    const d = new Date();
    expdays = expdays || 1;
    d.setTime(d.getTime() + expdays * 24 * 60 * 60 * 1000);
    expires = "expires=" + d.toUTCString() + ";";
  }
  document.cookie = cname + "=" + cvalue + ";" + expires + "path=/";
};

export const isSameOrigin = (origin: string): boolean => {
  if (origin === `http://localhost:5173`) return true;
  const matches = origin.match(/([^\\/.]+)\.com.sg/);
  if (matches && matches.length > 1) {
    return matches[1] === "tabla";
  }
  return false;
};

export const getWindowOrigin = (): string => {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return WEB2_BASE_URL;
};

export const getEnvOrigin = (): string => {
  switch (ENVIRONMENT) {
    default:
      return "http://localhost:5173";
  }
};

export const prefixCheck = (url: string): boolean => {
  if (url.startsWith("/") && !url.includes("://")) {
    return true;
  }
  return false;
};

/**
 * Description of the function.
 *
 * @param {Type} callback - Callback for this function.
 * @param {Type} maxRetries - No. of tries wanted to do.
 * @param {Type} initialDelayMs - Initial delay for each retry.
 * @param {Type} maxDelayMs - Maximum allowed delay.
 */

export async function fetchWithRetry<T>(
  callback: () => Promise<T>,
  maxRetries: number,
  initialDelayMs: number,
  maxDelayMs: number,
): Promise<T> {
  let retries = 0;
  let delay = initialDelayMs;

  while (retries < maxRetries) {
    try {
      return await callback();
    } catch (error) {
      retries++;
      if (retries >= maxRetries) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay = Math.min(delay * 2, maxDelayMs);
    }
  }

  throw new Error("Max retries exceeded");
}

export const isArrayWithElements = (payload: unknown): payload is unknown[] => {
  return Array.isArray(payload) && payload.length >= 1;
};

export function parseSortString(sortStr: string | undefined): number[] {
  if (typeof sortStr !== "string") return [];
  return sortStr
    .split(",")
    .map((item) => parseFloat(item.trim()))
    .filter((num) => !isNaN(num));
}

export function parseSizeInteger(
  input: string | undefined,
  defaultValue: number = 10,
): number {
  if (!input) return defaultValue;
  const num = Number(input);
  return Number.isInteger(num) && num > 0 ? num : defaultValue;
}

export function isValidNumber(size?: number): boolean {
  if (typeof size === "undefined") {
    //We never pass size as it is optional
    return true;
  }
  if (typeof size !== "number" || isNaN(size) || size < 0) {
    //We pass size but not valid number
    return false;
  }
  return true;
}

/**
 * Helper function to check if URL is a URL from the specified from parameter.
 * @param url URL to check.
 * @returns {boolean}
 */
export const isThisUrlFrom = (url: string, from: string): boolean => {
  const domain = new URL(url).hostname;
  return domain.includes(from);
};

export function mapArticlesOrder<T>(
  array: T[],
  order: string[],
  key: keyof T,
): T[] {
  array.sort((a, b) => {
    const A = a[key] as unknown as string;
    const B = b[key] as unknown as string;
    return order.indexOf(A) - order.indexOf(B);
  });
  return array;
}
//iteration determine how frequent it would return true, useful for ads.
export const displayAfter = (index: number, iteration: number): boolean => {
  return index > 0 && index % iteration === 0;
};

/**
 * Helper function to check if the user is on a mobile or tablet device.
 * @returns {boolean}
 */
export const isMobileOrTablet = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /iphone|ipad|ipod|android|blackberry|windows phone/g.test(
    userAgent,
  );
  const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(
    userAgent,
  );

  return isMobile || isTablet;
};

/**
 * Capitalizes the first letter of a string
 * @param str - The string to capitalize (can be null or undefined)
 * @returns The capitalized string or empty string if input is falsy
 */
export const capitalizeFirst = (str: string | null | undefined): string =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

/**
 * Formats a string for use as a sub-section name by replacing non-alphanumeric characters with hyphens
 * @param str - The string to format
 * @returns The formatted sub-section name
 */
export const formatSubSectionName = (str: string): string =>
  str.replace(/[^A-Za-z0-9]+/g, "-");

export function isValidTimestamp(v: number): boolean {
  if (!Number.isFinite(v)) return false;

  const now = Date.now();
  const oneYearMs = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds
  const minValidMs = now - oneYearMs; // only allow timestamps within the last year

  // Milliseconds
  const asMs = Math.floor(v);
  if (!isNaN(asMs) && asMs >= minValidMs && asMs <= now) return true;

  // Seconds
  const asSec = asMs * 1000;
  if (!isNaN(asSec) && asSec >= minValidMs && asSec <= now) return true;

  return false;
}

export function reactionCount(count: number): string {
  if (count >= 1_000_000_000) {
    return (count / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B"; // Billions
  } else if (count >= 1_000_000) {
    return (count / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M"; // Millions
  } else if (count >= 1_000) {
    return (count / 1_000).toFixed(1).replace(/\.0$/, "") + "K"; // Thousands
  }

  return count.toLocaleString();
}
